import type { HREmployee, HREmployeeDocument } from '@cloud/db'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AlertTriangle, Download, FolderOpen, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { ModuleGuard } from '@/components/module-guard'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Types & constants ─────────────────────────────────────────────────────────

const DOCUMENT_TYPES = [
  { value: 'id_card', label: 'ID Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'visa', label: 'Visa' },
  { value: 'work_permit', label: 'Work Permit' },
  { value: 'certificate', label: 'Certificate' },
  { value: 'medical', label: 'Medical' },
  { value: 'other', label: 'Other' },
] as const

const docTypeColors: Record<string, string> = {
  id_card: 'bg-blue-100 text-blue-800',
  passport: 'bg-green-100 text-green-800',
  visa: 'bg-purple-100 text-purple-800',
  work_permit: 'bg-orange-100 text-orange-800',
  certificate: 'bg-teal-100 text-teal-800',
  medical: 'bg-red-100 text-red-800',
  other: 'bg-gray-100 text-gray-800',
}

type DocType = (typeof DOCUMENT_TYPES)[number]['value']

// ── Schema ────────────────────────────────────────────────────────────────────

const uploadDocSchema = z.object({
  employeeId: z.number().min(1, 'Employee is required'),
  documentType: z.enum(['id_card', 'passport', 'visa', 'work_permit', 'certificate', 'medical', 'other']),
  name: z.string().min(1, 'Name is required'),
  fileUrl: z.string().min(1, 'File URL is required'),
  issuedDate: z.string().optional(),
  expiryDate: z.string().optional(),
  notes: z.string().optional(),
})

type UploadDocFormValues = z.infer<typeof uploadDocSchema>

export const Route = createFileRoute('/hr/documents/')({
  component: DocumentsPage,
})

function DocumentsPage() {
  return (
    <ModuleGuard moduleKey="hr">
      <DocumentsContent />
    </ModuleGuard>
  )
}

function DocumentsContent() {
  const [employeeFilter, setEmployeeFilter] = useState<string>('all')
  const [docTypeFilter, setDocTypeFilter] = useState<string>('all')
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; id: number | null }>({
    open: false,
    id: null,
  })

  const queryClient = useQueryClient()

  const { data: documentsData, isLoading } = useQuery(
    orpc.hr.listDocuments.queryOptions({
      input: {
        employeeId: employeeFilter !== 'all' ? Number(employeeFilter) : undefined,
        documentType: docTypeFilter !== 'all' ? (docTypeFilter as DocType) : undefined,
        page: 1,
        limit: 100,
      },
    }),
  )

  const { data: expiringData } = useQuery(
    orpc.hr.getExpiringDocuments.queryOptions({ input: { days: 30 } }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const documents = (documentsData?.data ?? []) as HREmployeeDocument[]
  const expiringDocuments = (expiringData ?? []) as (HREmployeeDocument & {
    employee?: HREmployee
  })[]
  const employees = (employeesData?.data ?? []) as HREmployee[]

  const createMutation = useMutation(
    orpc.hr.createDocument.mutationOptions({
      onSuccess: () => {
        toast.success('Document uploaded')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listDocuments']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'getExpiringDocuments']] })
        setIsFormOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMutation = useMutation(
    orpc.hr.deleteDocument.mutationOptions({
      onSuccess: () => {
        toast.success('Document deleted')
        queryClient.invalidateQueries({ queryKey: [['hr', 'listDocuments']] })
        queryClient.invalidateQueries({ queryKey: [['hr', 'getExpiringDocuments']] })
        setDeleteDialog({ open: false, id: null })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const getEmployeeName = (id: number) => {
    const emp = employees.find((e) => e.id === id)
    return emp ? `${emp.firstName} ${emp.lastName}` : `Employee #${id}`
  }

  const getDocTypeLabel = (type: string) => {
    return DOCUMENT_TYPES.find((d) => d.value === type)?.label ?? type
  }

  const getDaysUntilExpiry = (expiryDate: string) => {
    const today = new Date()
    const expiry = new Date(expiryDate)
    const diff = Math.ceil((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24))
    return diff
  }

  const handleDownload = async (documentId: number) => {
    try {
      const result = await queryClient.fetchQuery(
        orpc.hr.getDocumentDownloadUrl.queryOptions({ input: { id: documentId } }),
      )
      if (result?.url) {
        window.open(result.url, '_blank')
      }
    } catch (e) {
      toast.error('Failed to get download URL')
    }
  }

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Documents</h1>
          <p className="text-muted-foreground">Manage employee documents</p>
        </div>
        <Button onClick={() => setIsFormOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Upload Document
        </Button>
      </div>

      {expiringDocuments.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-yellow-500" />
            <h2 className="text-lg font-semibold">Expiring Soon</h2>
            <Badge variant="outline" className="text-yellow-600 border-yellow-400">
              {expiringDocuments.length} documents
            </Badge>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {expiringDocuments.map((doc) => {
              const daysLeft = doc.expiryDate ? getDaysUntilExpiry(doc.expiryDate) : null
              return (
                <Card key={doc.id} className="border-yellow-200 bg-yellow-50">
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-sm font-medium">{doc.name}</CardTitle>
                        <p className="text-xs text-muted-foreground mt-1">
                          {getEmployeeName(doc.employeeId)}
                        </p>
                      </div>
                      <Badge
                        className={`${docTypeColors[doc.documentType] ?? ''} text-xs`}
                        variant="outline"
                      >
                        {getDocTypeLabel(doc.documentType)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <p className="text-sm">
                        {daysLeft !== null && daysLeft <= 0 ? (
                          <span className="text-red-600 font-medium">Expired</span>
                        ) : daysLeft !== null ? (
                          <span className="text-yellow-700 font-medium">{daysLeft} days left</span>
                        ) : (
                          '-'
                        )}
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownload(doc.id)}
                      >
                        <Download className="h-3 w-3 mr-1" />
                        Download
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row gap-4">
            <Select value={employeeFilter} onValueChange={setEmployeeFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {employees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id.toString()}>
                    {emp.firstName} {emp.lastName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {DOCUMENT_TYPES.map((dt) => (
                  <SelectItem key={dt.value} value={dt.value}>
                    {dt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="space-y-2 p-6">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No documents found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Issued Date</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow key={doc.id}>
                    <TableCell>{getEmployeeName(doc.employeeId)}</TableCell>
                    <TableCell>
                      <Badge
                        className={`${docTypeColors[doc.documentType] ?? ''}`}
                        variant="outline"
                      >
                        {getDocTypeLabel(doc.documentType)}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">{doc.name}</TableCell>
                    <TableCell>
                      {doc.issuedDate ? new Date(doc.issuedDate).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>
                      {doc.expiryDate ? (
                        <span
                          className={
                            getDaysUntilExpiry(doc.expiryDate) <= 30
                              ? 'text-yellow-600 font-medium'
                              : ''
                          }
                        >
                          {new Date(doc.expiryDate).toLocaleDateString()}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDownload(doc.id)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => setDeleteDialog({ open: true, id: doc.id })}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <UploadDocumentDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        employees={employees}
        onSubmit={async (data) => {
          await createMutation.mutateAsync(data)
        }}
      />

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ open, id: null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Document</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this document? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteMutation.isPending}
              onClick={() => deleteDialog.id && deleteMutation.mutate({ id: deleteDialog.id })}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface UploadDocumentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: HREmployee[]
  onSubmit: (data: UploadDocFormValues) => Promise<void>
}

function UploadDocumentDialog({ open, onOpenChange, employees, onSubmit }: UploadDocumentDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<UploadDocFormValues>({
    resolver: zodResolver(uploadDocSchema),
    defaultValues: {
      name: '',
      fileUrl: '',
      issuedDate: '',
      expiryDate: '',
      notes: '',
    },
  })

  const handleSubmit = async (data: UploadDocFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Upload Document</DialogTitle>
          <DialogDescription>Upload a new employee document</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="employeeId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Employee *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select employee" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="documentType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Document Type *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(v as DocType)}
                    value={field.value ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {DOCUMENT_TYPES.map((dt) => (
                        <SelectItem key={dt.value} value={dt.value}>
                          {dt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Document Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Passport - John Doe" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="fileUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>File URL *</FormLabel>
                  <FormControl>
                    <Input placeholder="https://..." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="issuedDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Issued Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="expiryDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Expiry Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Additional notes..." rows={2} {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Uploading...' : 'Upload Document'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
